import clsx from 'clsx';
import { useState, useRef, useEffect, useCallback, CSSProperties } from 'react';
import { ChevronDown } from 'lucide-react';
import { createPortal } from 'react-dom';
import { formatDayOrdinal } from '../../utils/utils';
import styles from './DueDateCalendar.module.css';

interface DueDateCalendarProps {
  selectedDay: number;
  onSelectDay: (day: number) => void;
  /** Start of points range (inclusive). Must be <= pointsEndDay. Defaults to 7. */
  pointsStartDay?: number;
  /** End of points range (inclusive). Must be >= pointsStartDay. Defaults to 28. */
  pointsEndDay?: number;
  variant?: 'light' | 'dark';
  className?: string;
  disabled?: boolean;
}

export const DueDateCalendar = ({
  selectedDay,
  onSelectDay,
  pointsStartDay = 7,
  pointsEndDay = 28,
  variant = 'light',
  className,
  disabled = false,
}: DueDateCalendarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isDark = variant === 'dark';

  const getTriggerLabel = (day: number) => {
    const ordinal = formatDayOrdinal(day);
    return ordinal ? `${ordinal} of each month` : 'Select due date';
  };

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = Math.min(320, window.innerWidth - 24);
    const estimatedHeight = 290;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openAbove = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    // Prefer aligning to the trigger's left edge, clamped to viewport
    const clampedLeft = Math.max(12, Math.min(rect.left, window.innerWidth - popoverWidth - 12));

    const style: CSSProperties = openAbove
      ? {
          position: 'fixed',
          left: clampedLeft,
          width: popoverWidth,
          bottom: window.innerHeight - rect.top + 6,
          top: 'auto',
          zIndex: 10050,
        }
      : {
          position: 'fixed',
          left: clampedLeft,
          width: popoverWidth,
          top: rect.bottom + 6,
          bottom: 'auto',
          zIndex: 10050,
        };

    setPopoverStyle(style);
  }, []);

  const handleToggle = () => {
    if (disabled) return;
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    updatePosition();
    setIsOpen(true);
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Recalculate position on resize or scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleScrollOrResize = () => {
      updatePosition();
    };

    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);

    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
    };
  }, [isOpen, updatePosition]);

  const popover = isOpen
    ? createPortal(
        <div ref={dropdownRef} className={styles.calendarDropdown} style={popoverStyle}>
          {/* Points banner */}
          {/* <div className={styles.pointsBanner}>
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              className={styles.bannerIcon}
              aria-hidden='true'
            >
              <circle cx='8' cy='8' r='6' stroke='var(--primary-color, #0055ff)' strokeWidth='1.5' />
              <circle cx='8' cy='8' r='2.25' fill='var(--primary-color, #0055ff)' />
            </svg>
            <span className={styles.bannerText}>
              Days {pointsStartDay}–{pointsEndDay} earn points
            </span>
          </div> */}

          {/* 7-column day buttons (1 to 28) */}
          <div className={styles.calendarGrid}>
            {Array.from({ length: 28 }, (_, index) => index + 1).map((day) => {
              const isPointsDay = day >= pointsStartDay && day <= pointsEndDay;
              const isSelected = selectedDay === day;

              return (
                <button
                  key={day}
                  type='button'
                  className={clsx(
                    styles.calendarDay,
                    isPointsDay ? styles.pointsDay : styles.normalDay,
                    isSelected && styles.selected
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectDay(day);
                    setIsOpen(false);
                  }}
                  aria-label={`Select ${formatDayOrdinal(day)}${isPointsDay ? ', earns points' : ''}`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={clsx(styles.calendarContainer, className)}>
      <button
        ref={triggerRef}
        type='button'
        disabled={disabled}
        className={clsx(
          styles.dropdownTrigger,
          isDark && styles.dropdownTriggerDark,
          isOpen && styles.dropdownTriggerActive,
          disabled && styles.dropdownTriggerDisabled
        )}
        onClick={handleToggle}
        aria-haspopup='dialog'
        aria-expanded={isOpen}
      >
        <span className={clsx(styles.selectedDayText, isDark && styles.selectedDayTextDark)}>
          {getTriggerLabel(selectedDay)}
        </span>
        <ChevronDown
          size={18}
          stroke={isDark ? 'rgba(255, 255, 255, 0.7)' : '#0E0E0B'}
          className={clsx(styles.chevron, isOpen && styles.chevronOpen)}
          aria-hidden='true'
        />
      </button>

      {popover}
    </div>
  );
};
